import { Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material"
import { useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import { useSelector } from "react-redux";
import AlertMessages from "common/Alert/AlertMessages";
import { useNavigate, useParams } from "react-router-dom";
import { InventoryProfileService } from "subModule/src/core/services";
import { GetUnstockResponse, PurchaseOrderItems } from "subModule/src/services/inventory/interface";
import Loader from "layout/Loader";
import { useTranslation } from "react-i18next";
import { themes } from "theme/themes";
import { brandingInfo } from "features/server/brandingSlice";
const PrintPurchaseOrderUnstock: React.FC = () => {
    const params = useParams();
    const { t } = useTranslation();
    const branding = useSelector(brandingInfo);
    const navigate = useNavigate();
    const themeColors = branding?.attributes?.themeId === 100 ? branding?.attributes?.customTheme : (themes as any)[branding?.attributes?.themeId || 3];
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState<boolean>(true);
    const [unstock, setUnstock] = useState<any>();
    const columns = [
        { id: 'itemId' },
        { id: 'itemCode'},
        { id: 'unitId' },
        { id: 'itemName', label: t('Item'), numeric: false, disablePadding: false, type: 'text' },
        ...(params?.id ? [] : [{ id: 'baseItemQuantity', label: t('availableQuantity') }]),
        { id: 'quantity', label: t('Quantity'), numeric: false, disablePadding: false, type: 'number' },
        { id: 'unitTitle', label: t('unit') },
    ];
    useEffect(() => {
        if(loading) getUnstock();
    }, []);
    const getUnstock = () => {
        InventoryProfileService.getUnstockByPOID(params?.id).then((res: GetUnstockResponse) => {
            if(res.success) {
                setLoading(false);
                setUnstock((res.data as any));
            } else {
                setLoading(false);
                enqueueSnackbar(<AlertMessages text={res.message || "Something went wrong"} />, { variant: 'error' });
            }
        }).catch((err:any) => { console.error("===", err); });
    }
    return (
        loading ? <Loader /> : <Box>
            <Box className='max-w-[1100px] bg-white py-[10px] px-[20px] rounded-[8px] block mx-auto hide-on-print mb-4'>
                <Box className='flex justify-between items-center' id='form'>
                    <Typography className="font-Saira text-[12px] font-semibold" style={{ color: themeColors?.primary }}>{t('Unstock')}</Typography>
                    <Box className='flex gap-2'>
                        <Button className="secondary" variant="contained" style={{ color: themeColors?.primary }} disableElevation onClick={() => navigate(-1)}>{t('back')}</Button>
                        <Button className="primary" variant="contained" style={{ background: themeColors?.primary }} disableElevation onClick={() => window.print()}>{t('Print')}</Button>
                    </Box>
                </Box>
            </Box>
            <Box id="print-root" className='max-w-[1100px] bg-white p-[20px] rounded-[8px] block mx-auto'>
                <Box className='flex justify-end mb-2'>
                    <Box>
                        <Box className='block py-1 px-2 rounded-[8px]' style={{ background: themeColors?.appBg }}>
                            <Typography className="font-Saira text-[12px]">
                                <span className="font-bold" style={{ color: themeColors?.secondary }}>{t('Unstock Number')} # </span> 
                                <span className="font-semibold">{unstock?.unStockNumber || ''}</span>
                            </Typography>
                        </Box>
                    </Box>
                </Box>
                <Box className='flex justify-between py-3 px-2 rounded-[8px] mb-2' style={{ background: themeColors?.appBg }}>
                    <Typography className="font-Saira text-[12px] text-left">
                        <span className="italic font-medium">{t('storeName')}: </span> 
                        <span className="font-bold">{unstock?.storeTitle || ''}</span>
                    </Typography>
                    <Typography className="font-Saira text-[12px] text-left">
                        <span className="italic font-medium">{t('totalQuantity')}: </span> 
                        <span className="font-bold">{unstock?.totalQuantity || ''}</span>
                    </Typography>
                </Box>
                <Typography className="font-Saira text-[12px] font-semibold text-[#333843] text-left mb-2">{t('Unstock')}</Typography>
                <Box className='mb-4 w-[100%]' id="inventorytable">
                    <TableContainer>
                        <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle" size={'small'}>
                            <TableHead>
                                <TableRow sx={{ borderBottom: `1px solid #B8BDCB` }}>
                                    {columns.filter((column) => column.id !== 'itemId' && column.id !== 'itemCode' && column.id !== 'unitId').map((column) => (
                                        <TableCell
                                            key={column.id}
                                            align={column.numeric ? 'right' : 'left'}
                                            padding={column.disablePadding ? 'none' : 'normal'}
                                            sortDirection={false}
                                            className="font-bold text-center"
                                        >{column.label}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody sx={{ '& tr:nth-child(odd)': { background: `transparent !important` }, '& tr': {  borderBottom: `1px solid #B8BDCB` }, '& tr:last-child': { borderBottom: 0 } }}>
                                {(unstock?.unStockItem || []).map((row: PurchaseOrderItems, index: number) => {
                                    const labelId = `enhanced-table-checkbox-${index}`;
                                    return (
                                        <TableRow hover tabIndex={-1} key={row.id} sx={{ height: '36px' }}>
                                            {columns.filter((column) => column.id !== 'itemId' && column.id !== 'itemCode' && column.id !== 'unitId').map((column) => (
                                                <TableCell
                                                    key={column.id}
                                                    align={column.numeric ? 'right' : 'left'}
                                                    padding={column.disablePadding ? 'none' : 'normal'}
                                                    component={column.id === 'itemName' ? 'th' : undefined}
                                                    id={column.id === 'itemName' ? labelId : undefined}
                                                    scope={column.id === 'itemName' ? 'row' : undefined}
                                                    className="text-center"
                                                >{column.id === 'unitTitle' ? ((row as any)[column.id] || t('unit(s)'))
                                                    : (row as any)[column.id]
                                                }</TableCell>
                                            ))}
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </Box>
        </Box>
    )
}

export default PrintPurchaseOrderUnstock;